import React, { useState } from 'react';
import { Alert, App, Modal } from 'antd';

import { detachBundle } from '../../services/packages';
import { apiErrorCode, apiErrorMessage } from '../../utils/apiErrors';

/**
 * "Keep the cards, stop tracking…" from a bundle's group in the Card Library
 * (KPR-1133; epic KPE-035; the API is `POST …/packages/{key}/detach`).
 *
 * A plain confirm, not a typed word: nothing is deleted and every card, item
 * and setting stays as it is. What changes is what keepr says about them, and
 * the dialog says exactly that: the cards become this collection's own, no
 * update will be offered for the bundle, and the bundle can be installed from
 * Discover again later, beside these cards.
 *
 * A refusal stays in the dialog in the API's words, with the button still
 * armed; a failure part-way says that nothing is lost and to try again (the
 * record is kept, so a retry finishes). A bundle somebody else already
 * stopped tracking (404 `package_not_installed`) is the state the person
 * wanted: the dialog closes and the library reads again.
 *
 * Rendered only while open (the caller mounts it for one bundle at a time).
 */
export default function DetachBundleDialog( { bundleKey, bundleName, collectionId, onClose, onDetached } ) {
	const { message } = App.useApp();
	const [ busy, setBusy ] = useState( false );
	const [ problem, setProblem ] = useState( null );
	const name = bundleName || 'this bundle';

	const confirm = async () => {
		setBusy( true );
		setProblem( null );
		try {
			await detachBundle( collectionId, bundleKey );
			message.success( `The cards of ${ name } are now this collection’s own.` );
			onDetached?.();
			onClose();
		} catch ( err ) {
			if ( apiErrorCode( err ) === 'package_not_installed' ) {
				onDetached?.();
				onClose();
			} else {
				setProblem( apiErrorMessage( err, 'The cards could not be kept. Nothing was lost; try again.' ) );
				// A failure part-way may have marked some of them: read the library again.
				if ( err?.response?.data?.partial ) onDetached?.();
			}
		} finally {
			setBusy( false );
		}
	};

	return (
		<Modal
			open
			title={`Keep the cards of ${ name }`}
			okText='Keep the cards'
			cancelText='Cancel'
			okButtonProps={{ loading: busy }}
			cancelButtonProps={{ disabled: busy }}
			onOk={confirm}
			onCancel={busy ? undefined : onClose}
			destroyOnHidden
		>
			<div className='detach-bundle' data-testid='detach-bundle'>
				<p>
					The cards of <strong>{name}</strong> become this collection&rsquo;s own. They stay exactly as they are,
					with their records, settings and rules, and move to <strong>In this collection</strong>.
				</p>
				<p>
					keepr stops tracking the bundle here: no update will be offered for it, and Discover no longer lists this
					collection as having it. You can install the bundle again from Discover later; it is added beside these cards.
				</p>
				{ problem ? <Alert type='error' showIcon message={problem} data-testid='detach-bundle-error' /> : null }
			</div>
		</Modal>
	);
}
